import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import GroupDialogForm from '../GroupDialogForm';
import {
  actionButtonStyle,
  tacticGroupBoxStyle,
  tacticGroupCircularProgressStyle,
  tacticGroupCircularProgressSize,
  tacticGroupButtonBoxStyle,
} from '../TacticForm.style';
import { TacticFormTypes } from 'components/TacticForm/TacticForm.types';
import TooltipList from 'components/TooltipList/TooltipList';
import { ActionButton } from 'yoda-ui/components/BiogenLincDS/Buttons';
import Box from 'yoda-ui/components/Box';
import CircularProgress from 'yoda-ui/components/CircularProgress';
import CheckBoxYoda from 'yoda-ui/components/Form/CheckBoxYoda';
import DateRangePicker from 'yoda-ui/components/Form/DateRangePicker';
import SelectYoda from 'yoda-ui/components/Form/SelectYoda';
import TextYoda from 'yoda-ui/components/Form/TextYoda';
import Icon, { Icons } from 'yoda-ui/components/Icons/Icon';
import Loader from 'yoda-ui/components/Loader';
import Tooltip from 'yoda-ui/components/Tooltip';
import { YodaJustifyContent, YodaSpacing, YodaFontSize, YodaColors } from 'yoda-ui/yodaTheme';

export const VirtualMeeting: FC<TacticFormTypes> = ({
  tacticFormConfig,
  keyMessagesLoading,
  showTacticGroup,
  tacticGroupLoading,
  IVADocumentsLoading,
  showLegacyOrderingFields,
}) => {
  const flags = useFlags();
  const { t } = useTranslation();

  return (
    <>
      <Box marginBottom={ YodaSpacing.large }>
        <TextYoda { ...tacticFormConfig.title } />
      </Box>
      <Box marginBottom={ YodaSpacing.large }>
        <DateRangePicker { ...tacticFormConfig.date } />
      </Box>
      {
        showLegacyOrderingFields
            && <>
              <Box marginBottom={ YodaSpacing.large }>
                <CheckBoxYoda { ...tacticFormConfig.mandatory } />
              </Box>
              <Box marginBottom={ YodaSpacing.large }>
                <TextYoda { ...tacticFormConfig.actionOrder } />
              </Box>
              <Box marginBottom={ YodaSpacing.large }>
                <SelectYoda { ...tacticFormConfig.previousTactic } />
              </Box>
            </>
      }
      <Box marginBottom={ YodaSpacing.large }>
        <SelectYoda { ...tacticFormConfig.executingTeam } />
      </Box>
      {
        (!flags.mayaBdcfcsd1585EnableTacticGroup || !showTacticGroup) && showLegacyOrderingFields ? (
          <Box marginBottom={ YodaSpacing.large } marginTop={ YodaSpacing.xxSmall } display='flex'>
            <Box sx={ { width: '100%' } }>
              <SelectYoda { ...tacticFormConfig.parentTactic }/>
            </Box>
            <Tooltip title={ t('maya_tactic_form_parent_field_tooltip') || '' }>
              <Box paddingX={ YodaSpacing.xSmall } display='flex' sx={ { alignItems: 'center' } }>
                <Icon name={ Icons.info } sx={ { color: YodaColors.primaryBlue, cursor: 'pointer' } } fontSize="large"/>
              </Box>
            </Tooltip>
          </Box>
        ) : null
      }
      {
        flags.mayaBdcfcsd1585EnableTacticGroup && showTacticGroup && showLegacyOrderingFields
          ? (
            <Box marginBottom={ YodaSpacing.large } sx={ tacticGroupBoxStyle }>
              <SelectYoda { ...tacticFormConfig.tacticGroup } />
              {
                tacticGroupLoading ? (
                  <CircularProgress sx={ tacticGroupCircularProgressStyle } size={ tacticGroupCircularProgressSize }></CircularProgress>
                ) : null
              }
              <Box marginTop={ YodaFontSize.xxSmall } sx={ tacticGroupButtonBoxStyle }>
                <ActionButton sx={ actionButtonStyle } { ...tacticFormConfig.createTacticGroup }></ActionButton>
                {
                  tacticFormConfig.tacticGroupTooltip.listItems.length ? (
                    <TooltipList { ...tacticFormConfig.tacticGroupTooltip } >
                      <ActionButton sx={ actionButtonStyle } { ...tacticFormConfig.tacticGroupTooltipButton }></ActionButton>
                    </TooltipList>
                  ) : null
                }
              </Box>
            </Box>
          ) : null
      }
      {
        tacticFormConfig.assetName.defaultValue || flags.mayaAkma629EnableAssetNameField ? (
          <Box marginBottom={ YodaSpacing.large }>
            <TextYoda { ...tacticFormConfig.assetName } />
          </Box>
        ) : null
      }
      <Box marginBottom={ YodaSpacing.large }>
        {
          keyMessagesLoading
            ? (
              <Box
                display="flex"
                justifyContent={ YodaJustifyContent.center }
                position="absolute"
                paddingTop={ YodaSpacing.small }
                paddingBottom={ YodaSpacing.small }
              >
                <Loader center size={ 1 } />
              </Box>
            )
            : null
        }
        <SelectYoda
          { ...tacticFormConfig.keyMessage }
          disabled={ keyMessagesLoading || tacticFormConfig.keyMessage.disabled }
        />
      </Box>
      {
        flags.mayaAkma382EnableIvaTacticField
          ? (
            <Box marginBottom={ YodaSpacing.large }>
              {
                IVADocumentsLoading
                  ? (
                    <Box
                      display="flex"
                      justifyContent={ YodaJustifyContent.center }
                      position="absolute"
                      paddingTop={ YodaSpacing.small }
                      paddingBottom={ YodaSpacing.small }
                    >
                      <Loader center size={ 1 } />
                    </Box>
                  )
                  : null
              }
              <SelectYoda { ...tacticFormConfig.IVADocumentId } />
            </Box>
          )
          : null
      }
      <GroupDialogForm { ...tacticFormConfig.tacticGroupDialogForm } ></GroupDialogForm>
    </>
  );
};
